import { graphql, useStaticQuery, navigate } from 'gatsby'
import * as React from 'react'
import { createContext, useCallback, useEffect, useMemo, useState } from 'react'
import { SbLink, TranslatedSlug } from '../../../types'
import { I18nContextValue, LinkList, TranslatedLinkList, Translation, TranslationKey } from './interfaces'

import { defaultLanguage, INTERNAL_LANGUAGES, LANGUAGE, runtimeDefaultLanguage, setRuntimeDefaultLanguage } from '../../../../config/storyblok/languages'
import { detectUserLanguage, saveLanguagePreference } from '../../utils/languageDetection'
import { getTranslatedUrlFromBlok as resolveBlokUrl, getTranslatedUrlFromLink as resolveLinkUrl, resolveDetectedLanguagePath, resolveLanguageChangePath } from '../../utils/translatedUrls'

const I18nQuery = graphql`
  {
    datasource: allStoryblokDatasourceEntry(filter: { data_source: { eq: "strings" } }) {
      nodes {
        value
        name
        data_source_dimension
        dimension_value
      }
    }
    links: allStoryblokLink {
      nodes {
        slug
        uuid
        alternates {
          lang
          name
          path
        }
      }
    }
  }
`

export const I18nContext = createContext<I18nContextValue>({
  currentLanguage: defaultLanguage,
  translations: {},
  allLanguages: INTERNAL_LANGUAGES,
  defaultSlug: '',
  getTranslatedUrlFromLink: () => '',
  getTranslatedUrlFromBlok: () => '',
  changeLanguage: () => undefined,
})

export const I18nProvider = ({
  pageLanguage,
  translatedSlugs,
  children,
  defaultSlug,
  is404Page,
}: {
  pageLanguage: string
  children: JSX.Element
  translatedSlugs?: TranslatedSlug[]
  defaultSlug: string
  is404Page?: boolean
}) => {
  const allLanguages: LANGUAGE[] = INTERNAL_LANGUAGES
  const queryResult = useStaticQuery(I18nQuery)
  const [currentLanguage, setCurrentLanguage] = useState(
    pageLanguage === 'default' ? detectUserLanguage() : pageLanguage
  )

  // Handle initial language detection and routing
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const detectedLanguage = detectUserLanguage()
      setRuntimeDefaultLanguage(detectedLanguage)

      // Skip redirect for non-localized paths (e.g., docs)
      const isNonLocalizedPath = defaultSlug.startsWith('/docs')

      // Skip redirect for 404 pages - we want to keep the user on the original URL
      if (!is404Page && !isNonLocalizedPath && detectedLanguage !== pageLanguage && pageLanguage === 'default') {
        const newPath = resolveDetectedLanguagePath(detectedLanguage, translatedSlugs)
        if (newPath) {
          navigate(newPath, { replace: true })
        }
      }
    }
  }, [])

  const changeLanguage = useCallback((newLanguage: LANGUAGE) => {
    saveLanguagePreference(newLanguage)
    setRuntimeDefaultLanguage(newLanguage)
    setCurrentLanguage(newLanguage)

    const path = resolveLanguageChangePath(newLanguage, defaultSlug, translatedSlugs, is404Page)
    if (path !== null) {
      navigate(path, { replace: true })
    }
  }, [translatedSlugs, defaultSlug, is404Page])

  const { datasource, links } = queryResult as {
    links: LinkList
    datasource: any
  }

  // get translations from storyblok data source
  const translations = useMemo(() => {
    const mappedTranslations: Record<LANGUAGE, Translation> | {} = {}

    if (datasource) {
      datasource?.nodes?.forEach(source => {
        let key = source.data_source_dimension || defaultLanguage
        let value = source.dimension_value || source.value

        if (!mappedTranslations[key]) {
          mappedTranslations[key] = {}
        }
        mappedTranslations[key][source.name] = value
      })
    }
    return mappedTranslations
  }, [datasource])

  const translatedLinks = useMemo<TranslatedLinkList>(() => {
    const mappedLinks: TranslatedLinkList = {}

    if (links) {
      links?.nodes.forEach(source => {
        let key = source.uuid

        mappedLinks[key] = {
          defaultSlug: source.slug,
          translatedSlugs: source.alternates,
        }
      })
    }

    return mappedLinks
  }, [links])

  const getTranslatedUrlFromBlok = useCallback((fullSlug: string, translatedSlugs: TranslatedSlug[]) => {
    return resolveBlokUrl(fullSlug, translatedSlugs, currentLanguage)
  }, [currentLanguage])

  const getTranslatedUrlFromLink = useCallback(
    (link: SbLink) => {
      return resolveLinkUrl(link, currentLanguage, translatedLinks)
    },
    [currentLanguage, translatedLinks],
  )


  return (
    <I18nContext.Provider
      value={{
        allLanguages,
        currentLanguage: currentLanguage === 'default' ? defaultLanguage : currentLanguage,
        defaultSlug,
        getTranslatedUrlFromBlok,
        getTranslatedUrlFromLink,
        translations,
        changeLanguage,
      }}
    >
      {children}
    </I18nContext.Provider>
  )
}

export const useI18n = () => React.useContext(I18nContext)

export const useCurrentTranslations = (): Translation | null => {
  const { currentLanguage, translations } = useI18n()

  return translations[currentLanguage] || null
}

export const useTranslationString = (key: string) => {
  const translations = useCurrentTranslations()

  return translations?.[key] || key
}

export const useCurrentLanguageKey = () => {
  const { currentLanguage } = useI18n()

  return currentLanguage
}

export const useTranslation = () => {
  const translations = useCurrentTranslations()

  const t = React.useCallback(
    (key: TranslationKey | string) => (translations ? translations[key] || key : key),
    [translations],
  )

  return t
}

export const useTranslatedLinksFromBlok = () => {
  const { getTranslatedUrlFromBlok } = useI18n()

  return getTranslatedUrlFromBlok
}

